'use client';

import type { FC, ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { CardContent, CardHeader, CardTitle } from '@packages/shared/src/ui/Card/Card';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import type { PaylinkPaymentType } from '@packages/gql/generated/shopping/graphql';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { PaymentImage } from '../PaymentImage/PaymentImage.client';
import { DefaultTriggerRow, SelectableCard } from '../SelectableCard/SelectableCard';

const messages = defineMessages({
  // headline section
  creditcardHeadline: {
    id: 'PaymentCards.creditCard.Headline',
    defaultMessage: 'Kreditkarte',
  },
  mastercardHeadline: {
    id: 'PaymentCards.creditCard.Headline.Mastercard',
    defaultMessage: 'Kreditkarte - Mastercard',
  },
  visaHeadline: {
    id: 'PaymentCards.creditCard.Headline.Visa',
    defaultMessage: 'Kreditkarte - Visa',
  },
  amexHeadline: {
    id: 'PaymentCards.creditCard.Headline.AmericanExpress',
    defaultMessage: 'Kreditkarte - American Express',
  },
  sepaPaymentHeadline: {
    id: 'PaymentCards.sepaPaymentHeadline',
    defaultMessage: 'Onlineüberweisung',
  },
  sepaInstantPaymentHeadline: {
    id: 'PaymentCards.sepaInstantPaymentHeadline',
    defaultMessage: 'Echtzeitüberweisung',
  },
  cashOnDeliveryHeadline: {
    id: 'PaymentCards.cashOnDelivery.Headline',
    defaultMessage: 'Nachnahme',
  },
  idealHeadline: {
    id: 'PaymentCards.ideal.Headline',
    defaultMessage: 'iDEAL',
  },
  paydirektHeadline: {
    id: 'PaymentCards.paydirektHeadline',
    defaultMessage: 'giropay',
  },
  // inner text section
  creditCardText: {
    id: 'PaymentCards.creditCard.TriggerText',
    defaultMessage:
      'Sicheres Bestellen mit MasterCard, American Express, Visa, Diners Club oder Discover',
  },
  creditCardNoAmexText: {
    id: 'PaymentCards.creditCard.TriggerTextWithoutAmex',
    defaultMessage: 'Sicheres Bestellen mit MasterCard, Visa, Diners Club oder Discover',
  },
  mastercardText: {
    id: 'PaymentCards.creditCard.Trigger.Mastercard',
    defaultMessage: 'Sicheres Bezahlen mit Mastercard',
  },
  visaText: {
    id: 'PaymentCards.creditCard.Trigger.Visa',
    defaultMessage: 'Sicheres Bezahlen mit Visa',
  },
  amexText: {
    id: 'PaymentCards.creditCard.Trigger.AmericanExpress',
    defaultMessage: 'Sicheres Bezahlen mit American Express',
  },
  sepaPaymentText: {
    id: 'PaymentCards.sepaPaymentTriggerText',
    defaultMessage: 'Sicher direkt mit deinem Girokonto zahlen und Zeit sparen',
  },
  sepaInstantPaymentText: {
    id: 'PaymentCards.sepaInstantPaymentTriggerText',
    defaultMessage: 'Sicher und schnell direkt mit deinem Girokonto zahlen und Zeit sparen',
  },
  cashOnDeliveryDescription: {
    id: 'PaymentCards.cashOnDelivery.TriggerText',
    defaultMessage: 'Bezahle bequem direkt bei Lieferung deiner Ware.',
  },
  cashOnDeliveryIsActiveDescription: {
    id: 'PaymentCards.cashOnDelivery.TriggerTextActive',
    defaultMessage: 'Bezahle bequem direkt bei Lieferung deiner Ware.',
  },
  idealDescription: {
    id: 'PaymentCards.ideal.Description',
    defaultMessage: 'Meest gebruikte betaalmethode',
  },
  paydirektDescription: {
    id: 'PaymentCards.paydirekt.Description',
    defaultMessage: 'Sicher wie dein Girokonto.',
  },
  payPalHeadline: {
    id: 'PaymentCards.paypal.Headline',
    defaultMessage: 'PayPal',
  },
  paypalDescription: {
    id: 'PaymentCards.paypal.Description',
    defaultMessage: 'Schnell, einfach und unkompliziert',
  },
  weroHeadline: {
    id: 'PaymentCards.wero.Headline',
    defaultMessage: 'Wero',
  },
  weroDescription: {
    id: 'PaymentCards.wero.Description',
    defaultMessage: 'In Echtzeit Kaufbetrag begleichen',
  },
  securePaymentProviderHint: {
    id: 'PaymentCards.securePaymentProviderHint',
    defaultMessage:
      'Du wirst auf die Seite des Zahlungsanbieters weitergeleitet, um die Bezahlung abzuschließen.',
  },
  sepaPaymentIsActiveDescription: {
    id: 'PaymentCards.sepaPaymentActiveText',
    defaultMessage:
      'Du wirst im Anschluss auf die Seite unseres Zahlungsanbieters weitergeleitet. Über diese Seite kannst du dich bei deiner Bank anmelden und deine Zahlung freigeben. Halte bitte deine Anmeldedaten für dein Onlinebanking bereit.',
  },
  sepaInstantPaymentIsActiveDescription: {
    id: 'PaymentCards.sepaInstantPaymentActiveText',
    defaultMessage:
      'Du wirst im Anschluss auf die Seite unseres Zahlungsanbieters weitergeleitet. Über diese Seite kannst du dich bei deiner Bank anmelden und deine Zahlung freigeben. Halte bitte deine Anmeldedaten für dein Onlinebanking bereit.{break}<bold>Bitte beachte, dass deine Bank für diese Transaktion zusätzliche Gebühren verrechnen kann.</bold>',
  },
});

export interface PaymentCardProps {
  className?: string;
  isActive?: boolean;
  onSelect?: () => void;
  hideText?: boolean;
  option: PaylinkPaymentType; // to make cards reusable in the future we should use a generic payment type here
}

export const PaymentCard: FC<PaymentCardProps> = ({
  className,
  isActive,
  onSelect,
  option,
  hideText = false,
}) => {
  const { formatMessage } = useIntl();
  const {
    order: {
      payment: { americanExpress, clickToPay },
    },
    dynamicYieldEnabled,
  } = useConfig();

  const getCardElements = () => {
    switch (option) {
      case 'CREDITCARD':
        return {
          cardClassName: dynamicYieldEnabled ? 'dy_payment_creditcard' : undefined,
          headline: formatMessage(messages.creditcardHeadline),
          content: (
            <>
              {!hideText && (
                <div className="mr-auto hidden break-normal md:block">
                  {americanExpress
                    ? formatMessage(messages.creditCardText)
                    : formatMessage(messages.creditCardNoAmexText)}
                </div>
              )}
              <div className="relative flex flex-col items-center">
                {clickToPay && (
                  <div className="bg-color-background-base absolute -top-1.5 px-1.5">
                    <PaymentImage option="clicktopay" className="h-3.5 w-4" />
                  </div>
                )}
                <div
                  className={cn(
                    'flex flex-row flex-wrap items-center whitespace-normal md:whitespace-nowrap',
                    clickToPay && 'border-color-grey-dark rounded-md border p-2',
                  )}
                >
                  <PaymentImage option="mastercard" />
                  {americanExpress && <PaymentImage option="amex" />}
                  <PaymentImage option="visa" />
                  <PaymentImage option="diners" />
                  <PaymentImage option="discover" />
                </div>
              </div>
            </>
          ),
          notificationText: formatMessage(messages.securePaymentProviderHint),
        };
      case 'MASTERCARD':
        return {
          headline: formatMessage(messages.mastercardHeadline),
          content: (
            <>
              <div className="mr-auto break-normal">{formatMessage(messages.mastercardText)}</div>
              <PaymentImage
                className="shrink-0"
                option={clickToPay ? 'mastercard_clicktopay' : 'mastercard'}
              />
            </>
          ),
          notificationText: formatMessage(messages.securePaymentProviderHint),
        };
      case 'VISA':
        return {
          headline: formatMessage(messages.visaHeadline),
          content: (
            <>
              <div className="mr-auto break-normal">{formatMessage(messages.visaText)}</div>
              <PaymentImage className="shrink-0" option={clickToPay ? 'visa_clicktopay' : 'visa'} />
            </>
          ),
          notificationText: formatMessage(messages.securePaymentProviderHint),
        };
      case 'AMERICANEXPRESS':
        return {
          headline: formatMessage(messages.amexHeadline),
          content: (
            <>
              <div className="mr-auto break-normal">{formatMessage(messages.amexText)}</div>
              <PaymentImage className="shrink-0" option="amex" />
            </>
          ),
          notificationText: formatMessage(messages.securePaymentProviderHint),
        };
      case 'PAYPAL':
        return {
          cardClassName: dynamicYieldEnabled ? 'dy_payment_paypal' : undefined,
          headline: formatMessage(messages.payPalHeadline),
          content: (
            <>
              <div className="mr-auto break-normal">
                {formatMessage(messages.paypalDescription)}
              </div>
              <PaymentImage className="shrink-0" option="paypal" />
            </>
          ),
          notificationText: formatMessage(messages.securePaymentProviderHint),
        };
      case 'IDEAL':
        return {
          headline: formatMessage(messages.idealHeadline),
          content: (
            <>
              <div className="mr-auto break-normal">{formatMessage(messages.idealDescription)}</div>
              <PaymentImage className="shrink-0" option="ideal" />
            </>
          ),
          notificationText: formatMessage(messages.securePaymentProviderHint),
        };
      case 'SEPA':
        return {
          headline: formatMessage(messages.sepaPaymentHeadline),
          content: (
            <div className="mr-auto break-normal">{formatMessage(messages.sepaPaymentText)}</div>
          ),
          notificationText: formatMessage(messages.sepaPaymentIsActiveDescription),
        };
      case 'SEPAINSTANT':
        return {
          headline: formatMessage(messages.sepaInstantPaymentHeadline),
          content: (
            <div className="mr-auto break-normal">
              {formatMessage(messages.sepaInstantPaymentText)}
            </div>
          ),
          notificationText: formatMessage(messages.sepaInstantPaymentIsActiveDescription, {
            break: <br />,
            bold: (element: ReactNode) => <strong>{element}</strong>,
          }),
        };
      case 'WERO':
        return {
          headline: formatMessage(messages.weroHeadline),
          content: (
            <>
              <div className="mr-auto break-normal">{formatMessage(messages.weroDescription)}</div>
              <PaymentImage className="shrink-0" option="wero" />
            </>
          ),
          notificationText: formatMessage(messages.securePaymentProviderHint),
        };
      // more complex cards can be separated into their own components and returned immediately or set up like others
      default:
        return null;
    }
  };

  const card = getCardElements();

  if (!card) {
    return null;
  }

  return (
    <SelectableCard
      isActive={isActive}
      onSelect={onSelect}
      className={cn(className, card.cardClassName)}
    >
      <CardHeader hasDivider>
        <CardTitle>{card.headline}</CardTitle>
      </CardHeader>
      <CardContent className="pt-1.5 pb-2">
        <DefaultTriggerRow
          isActive={isActive}
          onSelect={onSelect}
          className="text-size-body-sm gap-2"
        >
          {card.content}
        </DefaultTriggerRow>
      </CardContent>
      {isActive && (
        <CardContent className="pt-1.5 pb-2">
          <Notification severity="info" variant="outline">
            {card.notificationText}
          </Notification>
        </CardContent>
      )}
    </SelectableCard>
  );
};
